import { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import { toast } from "sonner";
import { readDemoToken, readDemoLeadRef } from "../constants/demoMode";

const TOKEN_KEY = "epp_admin_token";
const STAFF_KEY = "epp_admin_staff";
const API = process.env.REACT_APP_BACKEND_URL || "http://localhost:8000";

const AdminContext = createContext(null);

export function AdminProvider({ children }) {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [staffInfo, setStaffInfo] = useState(() => {
    try { return JSON.parse(localStorage.getItem(STAFF_KEY)) || null; } catch { return null; }
  });

  const login = useCallback(async (password, email = null) => {
    const body = email ? { email, password } : { password };
    let res;
    try {
      res = await fetch(`${API}/api/admin/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
    } catch (e) {
      throw new Error("Impossible de joindre le serveur.");
    }
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      const msg = Array.isArray(err.detail) ? err.detail[0]?.msg || err.detail : err.detail;
      throw new Error(msg || "Identifiants incorrects");
    }
    const data = await res.json();
    localStorage.setItem(TOKEN_KEY, data.token);
    setToken(data.token);

    const meRes = await fetch(`${API}/api/admin/me`, {
      headers: { Authorization: `Bearer ${data.token}` },
    });
    if (meRes.ok) {
      const me = await meRes.json();
      localStorage.setItem(STAFF_KEY, JSON.stringify(me));
      setStaffInfo(me);
    }
    return data;
  }, []);

  const loginDemoSandbox = useCallback(async (password) => {
    const vitrine = readDemoToken();
    const lr = readDemoLeadRef();
    const headers = { "Content-Type": "application/json" };
    if (vitrine) headers["X-Demo-Session"] = `Bearer ${vitrine}`;
    const res = await fetch(`${API}/api/demo/admin-login`, {
      method: "POST",
      headers,
      body: JSON.stringify({ password: String(password), lead_ref: lr || undefined }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      const msg = Array.isArray(err.detail) ? err.detail[0]?.msg || err.detail : err.detail;
      throw new Error(msg || "Mot de passe sandbox incorrect");
    }
    const data = await res.json();
    const tok = data.access_token;
    localStorage.setItem(TOKEN_KEY, tok);
    setToken(tok);
    const meRes = await fetch(`${API}/api/admin/me`, {
      headers: { Authorization: `Bearer ${tok}` },
    });
    if (meRes.ok) {
      const me = await meRes.json();
      localStorage.setItem(STAFF_KEY, JSON.stringify(me));
      setStaffInfo(me);
    }
    return data;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(STAFF_KEY);
    setToken(null);
    setStaffInfo(null);
  }, []);

  useEffect(() => {
    if (!token) return;
    fetch(`${API}/api/admin/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => {
        if (r.ok) return r.json();
        throw new Error("expired");
      })
      .then((me) => {
        localStorage.setItem(STAFF_KEY, JSON.stringify(me));
        setStaffInfo(me);
      })
      .catch(() => {
        logout();
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const api = useCallback(
    async (path, options = {}) => {
      const headers = { ...options.headers };
      if (token) headers.Authorization = `Bearer ${token}`;
      let body = options.body;
      if (body && typeof body === "object" && !(body instanceof FormData)) {
        headers["Content-Type"] = "application/json";
        body = JSON.stringify(body);
      }
      const res = await fetch(`${API}${path}`, { ...options, headers, body });
      if (res.status === 401) {
        logout();
        throw new Error("Session expirée");
      }
      if (res.status === 403) {
        const err = await res.json().catch(() => ({}));
        const msg = err.detail || "Vous n'avez pas les permissions nécessaires pour cette action";
        toast.error(msg);
        throw new Error(msg);
      }
      return res;
    },
    [token, logout]
  );

  const hasPermission = useCallback(
    (perm) => {
      if (!perm || !staffInfo) return false;
      const perms = staffInfo.permissions || [];
      return perms.includes(perm);
    },
    [staffInfo]
  );

  const hasAnyPermission = useCallback(
    (perms) => {
      if (!staffInfo || !Array.isArray(perms) || perms.length === 0) return false;
      const userPerms = staffInfo.permissions || [];
      return perms.some((p) => userPerms.includes(p));
    },
    [staffInfo]
  );

  const value = useMemo(
    () => ({
      token,
      login,
      loginDemoSandbox,
      logout,
      api,
      isAuthenticated: !!token,
      staffInfo,
      role: staffInfo?.role || null,
      permissions: staffInfo?.permissions || [],
      storeIds: staffInfo?.store_ids || [],
      staffName: staffInfo?.name || "Utilisateur",
      hasPermission,
      hasAnyPermission,
    }),
    [token, login, loginDemoSandbox, logout, api, staffInfo, hasPermission, hasAnyPermission]
  );

  return (
    <AdminContext.Provider value={value}>
      {children}
    </AdminContext.Provider>
  );
}

export function useAdmin() {
  const ctx = useContext(AdminContext);
  if (!ctx) throw new Error("useAdmin must be used within AdminProvider");
  return ctx;
}
